export type FormatPrice = (
  amount: number,
  currencyCode: string,
  locale: string,
  removeZeroDecimals?: boolean,
  useGrouping?: boolean,
  currencyDisplay?: string,
  currencyFirst?: boolean,
  removeDecimalsAndRound?: boolean
) => string;

interface NumberFormatOptions {
  removeDecimalsAndRound?: boolean;
  removeZeroDecimals?: boolean;
  showDecimals: boolean;
  useGrouping: boolean;
  defaultOptions: Intl.NumberFormatOptions;
}

const getNumberFormatOptions = ({
  removeDecimalsAndRound,
  removeZeroDecimals,
  showDecimals,
  useGrouping,
  defaultOptions
}: NumberFormatOptions): Intl.NumberFormatOptions => {
  if (removeDecimalsAndRound) {
    return {
      ...defaultOptions,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping
    };
  }

  if (removeZeroDecimals) {
    return {
      ...defaultOptions,
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: 2,
      useGrouping
    };
  }

  return { ...defaultOptions, useGrouping };
};

/*
  Always use useExternalFormatPrice hook instead of this one directly
**/
const formatPrice: FormatPrice = (
  amount: number, // 1234.23
  currencyCode: string, // EUR | CAD | ...
  locale: string, // fr-CA | de-DE | en-US | ...
  removeZeroDecimals?: boolean,
  useGrouping = true,
  currencyDisplay?: string, // symbol | code | name
  currencyFirst?: boolean, // 1999 CHF --> CHF 1999
  removeDecimalsAndRound?: boolean
): string => {
  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: (currencyDisplay ?? 'symbol') as Intl.NumberFormatOptions['currencyDisplay']
  };

  const showDecimals = amount % 1 !== 0 || !amount.toFixed(2).endsWith('00');

  const numberFormat = new Intl.NumberFormat(
    locale,
    getNumberFormatOptions({
      removeDecimalsAndRound,
      removeZeroDecimals,
      showDecimals,
      useGrouping,
      defaultOptions
    })
  );

  if (currencyFirst) {
    const parts = numberFormat.formatToParts(amount);
    const currency = parts.find(p => p.type === 'currency')?.value ?? '';
    const rest = parts
      .filter(p => p.type !== 'currency' && p.type !== 'literal')
      .map(p => p.value)
      .join('');
    return `${currency} ${rest.trim()}`;
  }

  return numberFormat.format(amount);
};

export default formatPrice;

// Wut? Intl.NumberFormat ???
// https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
